import { Processing } from '@paywithextend/components'
import axios from 'axios'
import queryString from 'query-string'
import React from 'react'
import { match, matchPath } from 'react-router'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  ApproveBillPaySuccessToastMessage,
  CloseBulkVirtualCardCompleteToastMessage,
  CloseBulkVirtualCardInProgressToastMessage,
  CloseBulkVirtualCardPartialCompleteToastMessage,
  CloseVirtualCardToastMessage,
  CreateVirtualCardPendingFinalApprovalToastMessage,
  CreateVirtualCardToastMessage,
  DeactivateBulkVirtualCardCompleteToastMessage,
  DeactivateBulkVirtualCardInProgressToastMessage,
  DeactivateBulkVirtualCardPartialCompleteToastMessage,
  DeactivateVirtualCardToastMessage,
  RejectBillPaySuccessToastMessage,
  RequestVirtualCardToastMessage,
  ResendVirtualCardToastExpenseCategoriesRequiredButMissingMessage,
  ResendVirtualCardToastFailureMessage,
  ResendVirtualCardToastLimitMessage,
  ResendVirtualCardToastSuccessMessage,
  UpdateBulkVirtualCardCompleteToastMessage,
  UpdateBulkVirtualCardInProgressToastMessage,
  UpdateBulkVirtualCardPartialCompleteToastMessage,
  UpdateVirtualCardPendingFinalApprovalToastMessage,
  UpdateVirtualCardToastMessage,
} from 'src/app/Toast/Toast'
import { createSaveSelectedVirtualCardIdsAction } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import { SaasActionRequiredType } from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import { BulkResponse } from 'src/models/bulkUpload.models'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  TransactionStatus,
  TransactionType,
} from 'src/models/transaction.models'
import {
  BulkVirtualCardJobActionType,
  BulkVirtualCardJobStatus,
  ToastType,
  VirtualCardListModel,
  VirtualCardListResponse,
  VirtualCardSecureResponse,
  VirtualCardStatistics,
  VirtualCardType,
} from 'src/models/virtualCard.models'
import {
  VirtualCardPermissions,
  VirtualCardPermissionsResponse,
} from 'src/models/virtualCardPermissions.models'
import { closeModal, connectedPush } from 'src/navigation/Lnk'
import {
  VirtualCardDetailMatchParams,
  virtualCardDetailRouteProps,
} from 'src/navigation/routeProps'
import { yourCardsActiveUrl } from 'src/navigation/urls'
import {
  createSetIsBulkCloseVirtualCardsPopupOpen,
  createSetIsBulkDeactivateVirtualCardsPopupOpen,
  createSetIsBulkUpdateVirtualCardsFlyoutOpen,
} from 'src/redux/actions/app.actions'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAuthedJsonApiRequestConfig,
  selectAuthedJsonVaultRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { GetVirtualCardsRequest } from 'src/requests/getVirtualCards.request'
import { PatchBulkVirtualCardUpdateRequest } from 'src/requests/patchBulkVirtualCardUpdate.request'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import {
  PutVirtualCardApprovalRequest,
  PutVirtualCardRequest,
} from 'src/requests/putVirtualCard.request'
import {
  StatisticAggregateType,
  StatisticModelType,
  TransactionStatisticQueryFieldType,
} from 'src/requests/statistics.request'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { css } from 'styled-components'

export const GET_VIRTUAL_CARD_PERMISSIONS_INIT =
  'data/virtualCards/GET_VIRTUAL_CARD_PERMISSIONS_INIT'
export const GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS =
  'data/virtualCards/GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS'
export const GET_VIRTUAL_CARD_PERMISSIONS_FAILURE =
  'data/virtualCards/GET_VIRTUAL_CARD_PERMISSIONS_FAILURE'

export type GetVirtualCardPermissionsSuccessPayload =
  VirtualCardPermissionsResponse & {
    virtualCardId: string
  }

export interface GetVirtualCardPermissionsInitAction {
  type: typeof GET_VIRTUAL_CARD_PERMISSIONS_INIT
}
export interface GetVirtualCardPermissionsSuccessAction {
  type: typeof GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS
  payload: GetVirtualCardPermissionsSuccessPayload
}
export interface GetVirtualCardPermissionsFailureAction {
  type: typeof GET_VIRTUAL_CARD_PERMISSIONS_FAILURE
  payload: unknown
}

export type GetVirtualCardPermissionsActions =
  | GetVirtualCardPermissionsInitAction
  | GetVirtualCardPermissionsSuccessAction
  | GetVirtualCardPermissionsFailureAction

const createGetVirtualCardPermissionsInitAction =
  (): GetVirtualCardPermissionsInitAction => ({
    type: GET_VIRTUAL_CARD_PERMISSIONS_INIT,
  })
const createGetVirtualCardPermissionsSuccessAction = (
  payload: GetVirtualCardPermissionsSuccessPayload,
): GetVirtualCardPermissionsSuccessAction => ({
  type: GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS,
  payload,
})
const createGetVirtualCardPermissionsFailureAction = (
  response: unknown,
): GetVirtualCardPermissionsFailureAction => ({
  type: GET_VIRTUAL_CARD_PERMISSIONS_FAILURE,
  payload: response,
})

export const getVirtualCardPermissions = (
  virtualCardId: string,
): ThunkAction<
  Promise<
    | GetVirtualCardPermissionsFailureAction
    | GetVirtualCardPermissionsSuccessAction
  >,
  RootState,
  void,
  GetVirtualCardPermissionsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetVirtualCardPermissionsActions>,
    getState: () => RootState,
  ): Promise<
    | GetVirtualCardPermissionsFailureAction
    | GetVirtualCardPermissionsSuccessAction
  > => {
    dispatch(createGetVirtualCardPermissionsInitAction())
    return axios
      .get<GetVirtualCardPermissionsSuccessPayload>(
        `/virtualcards/${virtualCardId}/permissions`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetVirtualCardPermissionsSuccessAction({
              ...response.data,
              virtualCardId,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetVirtualCardPermissionsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_INIT =
  'data/virtualCards/GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_INIT'
export const GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_SUCCESS =
  'data/virtualCards/GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_SUCCESS'
export const GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_FAILURE =
  'data/virtualCards/GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_FAILURE'

export type GetVirtualCardPermissionsInBulkSuccessPayload = {
  data: Array<{
    permissions: VirtualCardPermissions[]
    virtualCardId: string
  }>
}

export interface GetVirtualCardPermissionsInBulkInitAction {
  type: typeof GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_INIT
}
export interface GetVirtualCardPermissionsInBulkSuccessAction {
  type: typeof GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_SUCCESS
  payload: GetVirtualCardPermissionsInBulkSuccessPayload
}
export interface GetVirtualCardPermissionsInBulkFailureAction {
  type: typeof GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_FAILURE
  payload: unknown
}

export type GetVirtualCardPermissionsInBulkActions =
  | GetVirtualCardPermissionsInBulkInitAction
  | GetVirtualCardPermissionsInBulkSuccessAction
  | GetVirtualCardPermissionsInBulkFailureAction

const createGetVirtualCardPermissionsInBulkInitAction =
  (): GetVirtualCardPermissionsInBulkInitAction => ({
    type: GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_INIT,
  })
const createGetVirtualCardPermissionsInBulkSuccessAction = (
  payload: GetVirtualCardPermissionsInBulkSuccessPayload,
): GetVirtualCardPermissionsInBulkSuccessAction => ({
  type: GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_SUCCESS,
  payload,
})
const createGetVirtualCardPermissionsInBulkFailureAction = (
  response: unknown,
): GetVirtualCardPermissionsInBulkFailureAction => ({
  type: GET_VIRTUAL_CARD_PERMISSIONS_IN_BULK_FAILURE,
  payload: response,
})

export const getVirtualCardPermissionsInBulk = (
  virtualCardIds: string[],
): ThunkAction<
  Promise<
    | GetVirtualCardPermissionsInBulkFailureAction
    | GetVirtualCardPermissionsInBulkSuccessAction
  >,
  RootState,
  void,
  GetVirtualCardPermissionsInBulkActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetVirtualCardPermissionsInBulkActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetVirtualCardPermissionsInBulkFailureAction
    | GetVirtualCardPermissionsInBulkSuccessAction
  > => {
    dispatch(createGetVirtualCardPermissionsInBulkInitAction())
    return axios
      .post<GetVirtualCardPermissionsInBulkSuccessPayload>(
        `/virtualcards/permissions`,
        JSON.stringify(virtualCardIds),
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetVirtualCardPermissionsInBulkSuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createGetVirtualCardPermissionsInBulkFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARD_INIT = 'data/virtualCards/GET_VIRTUAL_CARD_INIT'
export const GET_VIRTUAL_CARD_SUCCESS =
  'data/virtualCards/GET_VIRTUAL_CARD_SUCCESS'
export const GET_VIRTUAL_CARD_FAILURE =
  'data/virtualCards/GET_VIRTUAL_CARD_FAILURE'

export interface GetVirtualCardSuccessPayload {
  virtualCard: VirtualCardSecureResponse
}

export interface GetVirtualCardInitAction {
  type: typeof GET_VIRTUAL_CARD_INIT
}
export interface GetVirtualCardSuccessAction {
  type: typeof GET_VIRTUAL_CARD_SUCCESS
  payload: GetVirtualCardSuccessPayload
}
export interface GetVirtualCardFailureAction {
  type: typeof GET_VIRTUAL_CARD_FAILURE
  payload: unknown
}

export type GetVirtualCardActions =
  | GetVirtualCardInitAction
  | GetVirtualCardSuccessAction
  | GetVirtualCardFailureAction

const createGetVirtualCardInitAction = (): GetVirtualCardInitAction => ({
  type: GET_VIRTUAL_CARD_INIT,
})
export const createGetVirtualCardSuccessAction = (
  payload: GetVirtualCardSuccessPayload,
): GetVirtualCardSuccessAction => ({
  type: GET_VIRTUAL_CARD_SUCCESS,
  payload,
})
export const createGetVirtualCardFailureAction = (
  response: unknown,
): GetVirtualCardFailureAction => ({
  type: GET_VIRTUAL_CARD_FAILURE,
  payload: response,
})

export const getVirtualCard = (
  virtualCardId: string,
): ThunkAction<
  Promise<GetVirtualCardFailureAction | GetVirtualCardSuccessAction>,
  RootState,
  void,
  GetVirtualCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetVirtualCardActions>,
    getState: () => RootState,
  ): Promise<GetVirtualCardFailureAction | GetVirtualCardSuccessAction> => {
    dispatch(createGetVirtualCardInitAction())
    return axios
      .get<GetVirtualCardSuccessPayload>(
        `/virtualcards/${virtualCardId}`,
        selectAuthedJsonVaultRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createGetVirtualCardSuccessAction(response.data))
        },
        (err) => {
          return dispatch(createGetVirtualCardFailureAction(err.response.data))
        },
      )
  }
}

export const POST_VIRTUAL_CARD_INIT = 'data/virtualCards/POST_VIRTUAL_CARD_INIT'
export const POST_VIRTUAL_CARD_SUCCESS =
  'data/virtualCards/POST_VIRTUAL_CARD_SUCCESS'
export const POST_VIRTUAL_CARD_FAILURE =
  'data/virtualCards/POST_VIRTUAL_CARD_FAILURE'

export type PostVirtualCardSuccessPayload = {
  virtualCard: VirtualCardSecureResponse
}

export type PostVirtualCardFailurePayload = BackendErrorPayload<string> & {
  status: number
  exceptionType?: SaasActionRequiredType
}

export interface PostVirtualCardInitAction {
  type: typeof POST_VIRTUAL_CARD_INIT
}
export interface PostVirtualCardSuccessAction {
  type: typeof POST_VIRTUAL_CARD_SUCCESS
  payload: PostVirtualCardSuccessPayload
}
export interface PostVirtualCardFailureAction {
  type: typeof POST_VIRTUAL_CARD_FAILURE
  payload: PostVirtualCardFailurePayload
}

export type PostVirtualCardActions =
  | PostVirtualCardInitAction
  | PostVirtualCardSuccessAction
  | PostVirtualCardFailureAction

const createPostVirtualCardInitAction = (): PostVirtualCardInitAction => ({
  type: POST_VIRTUAL_CARD_INIT,
})
const createPostVirtualCardSuccessAction = (
  payload: PostVirtualCardSuccessPayload,
): PostVirtualCardSuccessAction => ({
  type: POST_VIRTUAL_CARD_SUCCESS,
  payload,
})
const createPostVirtualCardFailureAction = (
  body: BackendErrorPayload<string>,
  status: number,
): PostVirtualCardFailureAction => ({
  type: POST_VIRTUAL_CARD_FAILURE,
  payload: { ...body, status },
})

// TODO: switch the message sent based on targets? or switch in the creator
export const postVirtualCardAndOpenToastAndCloseFlyout = (
  body: Partial<PostVirtualCardRequest>,
  isCreation?: boolean,
  overrideToast?: boolean,
  showFinalApprovalToast?: boolean,
): ThunkAction<
  Promise<PostVirtualCardSuccessAction | PostVirtualCardFailureAction>,
  RootState,
  void,
  PostVirtualCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostVirtualCardActions>,
    getState: () => RootState,
  ): Promise<PostVirtualCardSuccessAction | PostVirtualCardFailureAction> => {
    dispatch(createPostVirtualCardInitAction())
    return axios
      .post<PostVirtualCardSuccessPayload>(
        `/virtualcards`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const createPostVirtualCardSuccessActionDispatched = dispatch(
            createPostVirtualCardSuccessAction(response.data),
          )
          dispatch(closeModal())
          if (!overrideToast) {
            if (isCreation) {
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: showFinalApprovalToast ? (
                    <CreateVirtualCardPendingFinalApprovalToastMessage />
                  ) : (
                    <CreateVirtualCardToastMessage
                      virtualCard={
                        new VirtualCardListModel(response.data.virtualCard)
                      }
                      users={selectUserListModelsFromState(getState())}
                    />
                  ),
                }),
              )
            } else {
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <RequestVirtualCardToastMessage
                      virtualCard={
                        new VirtualCardListModel(response.data.virtualCard)
                      }
                      users={selectUserListModelsFromState(getState())}
                    />
                  ),
                }),
              )
            }
          }
          return createPostVirtualCardSuccessActionDispatched
        },
        (err) => {
          const createPostVirtualCardFailureActionDispatched = dispatch(
            createPostVirtualCardFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
          return createPostVirtualCardFailureActionDispatched
        },
      )
  }
}

export const POST_BULK_VIRTUAL_CARD_INIT =
  'data/virtualCards/POST_BULK_VIRTUAL_CARD_INIT'
export const POST_BULK_VIRTUAL_CARD_SUCCESS =
  'data/virtualCards/POST_BULK_VIRTUAL_CARD_SUCCESS'
export const POST_BULK_VIRTUAL_CARD_COMPLETION =
  'data/virtualCards/POST_BULK_VIRTUAL_CARD_COMPLETION'
export const POST_BULK_VIRTUAL_CARD_FAILURE =
  'data/virtualCards/POST_BULK_VIRTUAL_CARD_FAILURE'

export type PostBulkVirtualCardSuccessPayload = {
  bulkVirtualCardPush: BulkResponse
  displayName?: string
  invitingUsers?: boolean
}

export type PostBulkVirtualCardFailurePayload = BackendErrorPayload<string> & {
  status: number
  exceptionType?: SaasActionRequiredType
}

export interface PostBulkVirtualCardInitAction {
  type: typeof POST_BULK_VIRTUAL_CARD_INIT
}
export interface PostBulkVirtualCardSuccessAction {
  type: typeof POST_BULK_VIRTUAL_CARD_SUCCESS
  payload: PostBulkVirtualCardSuccessPayload
}
export interface PostBulkVirtualCardCompletionAction {
  type: typeof POST_BULK_VIRTUAL_CARD_COMPLETION
  payload: string
}
export interface PostBulkVirtualCardFailureAction {
  type: typeof POST_BULK_VIRTUAL_CARD_FAILURE
  payload: PostBulkVirtualCardFailurePayload
}

export const createPostBulkVirtualCardInitAction =
  (): PostBulkVirtualCardInitAction => ({
    type: POST_BULK_VIRTUAL_CARD_INIT,
  })
export const createPostBulkVirtualCardSuccessAction = (
  payload: PostBulkVirtualCardSuccessPayload,
): PostBulkVirtualCardSuccessAction => ({
  type: POST_BULK_VIRTUAL_CARD_SUCCESS,
  payload,
})
export const createPostBulkVirtualCardCompletionAction = (
  payload: string,
): PostBulkVirtualCardCompletionAction => ({
  type: POST_BULK_VIRTUAL_CARD_COMPLETION,
  payload,
})
export const createPostBulkVirtualCardFailureAction = (
  body: BackendErrorPayload<string>,
  status: number,
): PostBulkVirtualCardFailureAction => ({
  type: POST_BULK_VIRTUAL_CARD_FAILURE,
  payload: { ...body, status },
})

export type PostBulkVirtualCardActions =
  | PostBulkVirtualCardInitAction
  | PostBulkVirtualCardSuccessAction
  | PostBulkVirtualCardFailureAction
  | PostBulkVirtualCardCompletionAction

export const postBulkVirtualCardAndOpenToastAndCloseFlyout = (
  body: Partial<PostBulkVirtualCardRequest>,
  invitingUsers?: boolean,
): ThunkAction<
  Promise<PostBulkVirtualCardSuccessAction | PostBulkVirtualCardFailureAction>,
  RootState,
  void,
  PostBulkVirtualCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostBulkVirtualCardActions>,
    getState: () => RootState,
  ): Promise<
    PostBulkVirtualCardSuccessAction | PostBulkVirtualCardFailureAction
  > => {
    dispatch(createPostBulkVirtualCardInitAction())
    return axios
      .post<PostBulkVirtualCardSuccessPayload>(
        `/virtualcards/bulk`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const createPostBulkVirtualCardSuccessActionDispatched = dispatch(
            createPostBulkVirtualCardSuccessAction({
              ...response.data,
              displayName: body.displayName,
              invitingUsers: invitingUsers,
            }),
          )
          dispatch(closeModal())
          return createPostBulkVirtualCardSuccessActionDispatched
        },
        (err) => {
          const createPostBulkVirtualCardFailureActionDispatched = dispatch(
            createPostBulkVirtualCardFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
          return createPostBulkVirtualCardFailureActionDispatched
        },
      )
  }
}

export const PUT_VIRTUAL_CARD_INIT = 'data/virtualCards/PUT_VIRTUAL_CARD_INIT'
export const PUT_VIRTUAL_CARD_SUCCESS =
  'data/virtualCards/PUT_VIRTUAL_CARD_SUCCESS'
export const PUT_VIRTUAL_CARD_FAILURE =
  'data/virtualCards/PUT_VIRTUAL_CARD_FAILURE'

export type PutVirtualCardSuccessPayload = {
  virtualCard: VirtualCardSecureResponse
}

export type PutVirtualCardFailurePayload = {
  errorMessage: BackendErrorPayload<string> & {
    exceptionType?: SaasActionRequiredType
  }
  status: number
  exceptionType?: SaasActionRequiredType
}

export interface PutVirtualCardInitAction {
  type: typeof PUT_VIRTUAL_CARD_INIT
}
export interface PutVirtualCardSuccessAction {
  type: typeof PUT_VIRTUAL_CARD_SUCCESS
  payload: PutVirtualCardSuccessPayload
}
export interface PutVirtualCardFailureAction {
  type: typeof PUT_VIRTUAL_CARD_FAILURE
  payload: PutVirtualCardFailurePayload
}

export type PutVirtualCardActions =
  | PutVirtualCardInitAction
  | PutVirtualCardSuccessAction
  | PutVirtualCardFailureAction

const createPutVirtualCardInitAction = (): PutVirtualCardInitAction => ({
  type: PUT_VIRTUAL_CARD_INIT,
})
const createPutVirtualCardSuccessAction = (
  payload: PutVirtualCardSuccessPayload,
): PutVirtualCardSuccessAction => ({
  type: PUT_VIRTUAL_CARD_SUCCESS,
  payload,
})
const createPutVirtualCardFailureAction = (
  errorMessage: BackendErrorPayload<string> & {
    exceptionType?: SaasActionRequiredType
  },
  status: number,
): PutVirtualCardFailureAction => ({
  type: PUT_VIRTUAL_CARD_FAILURE,
  payload: {
    errorMessage,
    status,
    exceptionType: errorMessage.exceptionType,
  },
})

export const putVirtualCardAndOpenToastAndCloseFlyout = (
  id: string,
  body: Partial<PutVirtualCardRequest>,
  openToast = true,
  toastType?: ToastType,
): ThunkAction<Promise<void>, RootState, void, PutVirtualCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutVirtualCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutVirtualCardInitAction())
    return axios
      .put<PutVirtualCardSuccessPayload>(
        `/virtualcards/${id}`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutVirtualCardSuccessAction(response.data))
          dispatch(createSaveSelectedVirtualCardIdsAction([]))
          dispatch(closeModal())
          if (openToast) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage:
                  toastType === ToastType.ApproveRequestOrCreate ? (
                    <CreateVirtualCardPendingFinalApprovalToastMessage />
                  ) : toastType === ToastType.ApproveUpdate ? (
                    <UpdateVirtualCardPendingFinalApprovalToastMessage />
                  ) : (
                    <UpdateVirtualCardToastMessage />
                  ),
              }),
            )
          }
        },
        (err) => {
          dispatch(
            createPutVirtualCardFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
        },
      )
  }
}

export const putVirtualCardFinalApprovalAndOpenToastAndCloseFlyout = (
  id: string,
  body: PutVirtualCardApprovalRequest,
  openToast = true,
  toastType?: ToastType,
  vendor?: string,
): ThunkAction<Promise<void>, RootState, void, PutVirtualCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutVirtualCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutVirtualCardInitAction())
    return axios
      .put<PutVirtualCardSuccessPayload>(
        `/virtualcards/${id}/approve`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutVirtualCardSuccessAction(response.data))
          dispatch(closeModal())
          if (openToast) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage:
                  toastType === ToastType.ApproveRequestOrCreate ? (
                    <CreateVirtualCardPendingFinalApprovalToastMessage />
                  ) : toastType === ToastType.ApproveUpdate ? (
                    <UpdateVirtualCardPendingFinalApprovalToastMessage />
                  ) : toastType === ToastType.BillPayApprove ? (
                    <ApproveBillPaySuccessToastMessage vendor={vendor || ''} />
                  ) : (
                    <UpdateVirtualCardToastMessage />
                  ),
              }),
            )
          }
        },
        (err) => {
          dispatch(
            createPutVirtualCardFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
        },
      )
  }
}

export const PUT_VIRTUAL_CARD_CANCEL_INIT =
  'data/virtualCards/PUT_VIRTUAL_CARD_CANCEL_INIT'
export const PUT_VIRTUAL_CARD_CANCEL_SUCCESS =
  'data/virtualCards/PUT_VIRTUAL_CARD_CANCEL_SUCCESS'
export const PUT_VIRTUAL_CARD_CANCEL_FAILURE =
  'data/virtualCards/PUT_VIRTUAL_CARD_CANCEL_FAILURE'

export type PutVirtualCardCancelSuccessPayload = {
  virtualCard: VirtualCardSecureResponse
}
export type PutVirtualCardCancelFailurePayload = BackendErrorPayload<string>

export interface PutVirtualCardCancelInitAction {
  type: typeof PUT_VIRTUAL_CARD_CANCEL_INIT
}
export interface PutVirtualCardCancelSuccessAction {
  type: typeof PUT_VIRTUAL_CARD_CANCEL_SUCCESS
  payload: PutVirtualCardCancelSuccessPayload
}
export interface PutVirtualCardCancelFailureAction {
  type: typeof PUT_VIRTUAL_CARD_CANCEL_FAILURE
  payload: PutVirtualCardCancelFailurePayload
}

export type PutVirtualCardCancelActions =
  | PutVirtualCardCancelInitAction
  | PutVirtualCardCancelSuccessAction
  | PutVirtualCardCancelFailureAction

const createPutVirtualCardCancelInitAction =
  (): PutVirtualCardCancelInitAction => ({
    type: PUT_VIRTUAL_CARD_CANCEL_INIT,
  })
const createPutVirtualCardCancelSuccessAction = (
  payload: PutVirtualCardCancelSuccessPayload,
): PutVirtualCardCancelSuccessAction => ({
  type: PUT_VIRTUAL_CARD_CANCEL_SUCCESS,
  payload,
})
const createPutVirtualCardCancelFailureAction = (
  response: PutVirtualCardCancelFailurePayload,
): PutVirtualCardCancelFailureAction => ({
  type: PUT_VIRTUAL_CARD_CANCEL_FAILURE,
  payload: response,
})

export const putVirtualCardCancelAndCloseModalOrRedirect = (
  id: string,
  isPending: boolean,
  hideToast?: boolean,
  // Query-param overlays close by dropping their URL param, not by navigating
  // to a reconstructed background. Callers on the modal-route model pass
  // `useModalRoute().close` here; legacy callers omit it and fall back to the
  // connected-react-router `closeModal()`.
  onClose?: () => void,
): ThunkAction<Promise<void>, RootState, void, PutVirtualCardCancelActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutVirtualCardCancelActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutVirtualCardCancelInitAction())
    return axios
      .put<PutVirtualCardCancelSuccessPayload>(
        `/virtualcards/${id}/cancel`,
        undefined,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutVirtualCardCancelSuccessAction(response.data))
          const backgroundLocation =
            selectCalculatedBackgroundLocation(getState())
          const virtualCardDetailMatch =
            matchPath<VirtualCardDetailMatchParams>(
              backgroundLocation.pathname,
              virtualCardDetailRouteProps,
            ) as match<VirtualCardDetailMatchParams>
          if (virtualCardDetailMatch !== null && isPending) {
            dispatch(connectedPush(yourCardsActiveUrl()))
          } else {
            if (onClose !== undefined) {
              onClose()
            } else {
              dispatch(closeModal())
            }
            if (!hideToast) {
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <DeactivateVirtualCardToastMessage
                      virtualCard={
                        new VirtualCardListModel(response.data.virtualCard)
                      }
                    />
                  ),
                }),
              )
            }
          }
        },
        (err) => {
          dispatch(createPutVirtualCardCancelFailureAction(err.response.data))
        },
      )
  }
}

export const SET_VIRTUAL_CARD_FAILURE =
  'data/virtualCards/SET_VIRTUAL_CARD_FAILURE'

export interface SetVirtualCardFailureAction {
  type: typeof SET_VIRTUAL_CARD_FAILURE
}

export const createSetVirtualCardFailure = (): SetVirtualCardFailureAction => ({
  type: SET_VIRTUAL_CARD_FAILURE,
})

export const PUT_VIRTUAL_CARD_REJECT_INIT =
  'data/virtualCards/PUT_VIRTUAL_CARD_REJECT_INIT'
export const PUT_VIRTUAL_CARD_REJECT_SUCCESS =
  'data/virtualCards/PUT_VIRTUAL_CARD_REJECT_SUCCESS'
export const PUT_VIRTUAL_CARD_REJECT_FAILURE =
  'data/virtualCards/PUT_VIRTUAL_CARD_REJECT_FAILURE'

export type PutVirtualCardRejectSuccessPayload = {
  virtualCard: VirtualCardSecureResponse
}

export type PutVirtualCardRejectFailurePayload = {
  errorMessage: BackendErrorPayload<string>
  status: number
}

export interface PutVirtualCardRejectInitAction {
  type: typeof PUT_VIRTUAL_CARD_REJECT_INIT
}
export interface PutVirtualCardRejectSuccessAction {
  type: typeof PUT_VIRTUAL_CARD_REJECT_SUCCESS
  payload: PutVirtualCardRejectSuccessPayload
}
export interface PutVirtualCardRejectFailureAction {
  type: typeof PUT_VIRTUAL_CARD_REJECT_FAILURE
  payload: PutVirtualCardRejectFailurePayload
}

export type PutVirtualCardRejectActions =
  | PutVirtualCardRejectInitAction
  | PutVirtualCardRejectSuccessAction
  | PutVirtualCardRejectFailureAction

const createPutVirtualCardRejectInitAction =
  (): PutVirtualCardRejectInitAction => ({
    type: PUT_VIRTUAL_CARD_REJECT_INIT,
  })
const createPutVirtualCardRejectSuccessAction = (
  payload: PutVirtualCardRejectSuccessPayload,
): PutVirtualCardRejectSuccessAction => ({
  type: PUT_VIRTUAL_CARD_REJECT_SUCCESS,
  payload,
})
const createPutVirtualCardRejectFailureAction = (
  errorMessage: BackendErrorPayload<string>,
  status: number,
): PutVirtualCardRejectFailureAction => ({
  type: PUT_VIRTUAL_CARD_REJECT_FAILURE,
  payload: {
    errorMessage,
    status,
  },
})

export const putVirtualCardRejectAndOpenToastAndCloseModal = (
  id: string,
  pendingRevisionId?: string,
  cardType?: VirtualCardType,
): ThunkAction<Promise<void>, RootState, void, PutVirtualCardRejectActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutVirtualCardRejectActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutVirtualCardRejectInitAction())
    return axios
      .put<PutVirtualCardRejectSuccessPayload>(
        `/virtualcards/${id}/reject`,
        {
          pendingRevisionId: pendingRevisionId,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutVirtualCardRejectSuccessAction(response.data))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(
              // TODO: new toast?
              true,
              {
                toastMessage:
                  cardType === VirtualCardType.BillPay ? (
                    <RejectBillPaySuccessToastMessage />
                  ) : (
                    <UpdateVirtualCardToastMessage />
                  ),
              },
            ),
          )
        },
        (err) => {
          dispatch(
            createPutVirtualCardRejectFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
        },
      )
  }
}

export const PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT =
  'data/virtualCards/PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT'
export const PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS =
  'data/virtualCards/PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS'
export const PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE =
  'data/virtualCards/PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE'

export type PutVirtualCardCancelUpdateSuccessPayload = {
  virtualCard: VirtualCardSecureResponse
}

export interface PutVirtualCardCancelUpdateInitAction {
  type: typeof PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT
}
export interface PutVirtualCardCancelUpdateSuccessAction {
  type: typeof PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS
  payload: PutVirtualCardCancelUpdateSuccessPayload
}
export interface PutVirtualCardCancelUpdateFailureAction {
  type: typeof PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE
  payload: unknown
}

export type PutVirtualCardCancelUpdateActions =
  | PutVirtualCardCancelUpdateInitAction
  | PutVirtualCardCancelUpdateSuccessAction
  | PutVirtualCardCancelUpdateFailureAction

const createPutVirtualCardCancelUpdateInitAction =
  (): PutVirtualCardCancelUpdateInitAction => ({
    type: PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT,
  })
const createPutVirtualCardCancelUpdateSuccessAction = (
  payload: PutVirtualCardCancelUpdateSuccessPayload,
): PutVirtualCardCancelUpdateSuccessAction => ({
  type: PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS,
  payload,
})
const createPutVirtualCardCancelUpdateFailureAction = (
  response: unknown,
): PutVirtualCardCancelUpdateFailureAction => ({
  type: PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE,
  payload: response,
})

export const putVirtualCardCancelUpdateAndOpenToastAndCloseModal = (
  id: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PutVirtualCardCancelUpdateActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutVirtualCardCancelUpdateActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutVirtualCardCancelUpdateInitAction())
    return axios
      .put<PutVirtualCardCancelUpdateSuccessPayload>(
        `/virtualcards/${id}/cancelupdate`,
        undefined,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutVirtualCardCancelUpdateSuccessAction(response.data))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(
              // TODO: new toast?
              true,
              {
                toastMessage: <UpdateVirtualCardToastMessage />,
              },
            ),
          )
        },
        (err) => {
          dispatch(
            createPutVirtualCardCancelUpdateFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARDS_INIT = 'data/virtualCards/GET_VIRTUAL_CARDS_INIT'
export const GET_VIRTUAL_CARDS_SUCCESS =
  'data/virtualCards/GET_VIRTUAL_CARDS_SUCCESS'
export const GET_VIRTUAL_CARDS_FAILURE =
  'data/virtualCards/GET_VIRTUAL_CARDS_FAILURE'

export interface GetVirtualCardsSuccessPayload {
  virtualCards: VirtualCardListResponse[]
  pagination: PaginationResponse
}

export interface GetVirtualCardsInitAction {
  type: typeof GET_VIRTUAL_CARDS_INIT
}
export interface GetVirtualCardsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_SUCCESS
  payload: GetVirtualCardsSuccessPayload
}
export interface GetVirtualCardsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_FAILURE
  payload: unknown
}

export type GetVirtualCardsActions =
  | GetVirtualCardsInitAction
  | GetVirtualCardsSuccessAction
  | GetVirtualCardsFailureAction

export const createGetVirtualCardsInitAction =
  (): GetVirtualCardsInitAction => ({
    type: GET_VIRTUAL_CARDS_INIT,
  })
const createGetVirtualCardsSuccessAction = (
  payload: GetVirtualCardsSuccessPayload,
): GetVirtualCardsSuccessAction => ({
  type: GET_VIRTUAL_CARDS_SUCCESS,
  payload,
})
const createGetVirtualCardsFailureAction = (
  err: unknown,
): GetVirtualCardsFailureAction => ({
  type: GET_VIRTUAL_CARDS_FAILURE,
  payload: err,
})

export const getVirtualCards = (
  queryParameters: GetVirtualCardsRequest,
): ThunkAction<
  Promise<GetVirtualCardsSuccessAction | GetVirtualCardsFailureAction>,
  RootState,
  void,
  GetVirtualCardsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetVirtualCardsActions>,
    getState: () => RootState,
  ): Promise<GetVirtualCardsSuccessAction | GetVirtualCardsFailureAction> => {
    dispatch(createGetVirtualCardsInitAction())
    return axios
      .get<GetVirtualCardsSuccessPayload>(
        `/virtualcards?${queryString.stringify(queryParameters)}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          return dispatch(createGetVirtualCardsSuccessAction(response.data))
        },
        (err) => {
          return dispatch(createGetVirtualCardsFailureAction(err.response.data))
        },
      )
  }
}

export const GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT =
  'data/virtualCards/GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT'
export const GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS =
  'data/virtualCards/GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS'
export const GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE =
  'data/virtualCards/GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE'

export interface GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessPayload {
  statistics: Array<{ value: number }>
  virtualCardIds: string[]
  statisticKeys: Array<keyof VirtualCardStatistics>
}
export interface GetVirtualCardsStatisticsClearedTransactionDebitAmountInitAction {
  type: typeof GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT
}
export interface GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS
  payload: GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessPayload
}
export interface GetVirtualCardsStatisticsClearedTransactionDebitAmountFailureAction {
  type: typeof GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE
  payload: unknown
}

const createGetVirtualCardsStatisticsClearedTransactionDebitAmountInitAction =
  (): GetVirtualCardsStatisticsClearedTransactionDebitAmountInitAction => ({
    type: GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT,
  })
const createGetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessAction =
  (
    payload: GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessPayload,
  ): GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessAction => ({
    type: GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS,
    payload,
  })
const createGetVirtualCardsStatisticsClearedTransactionDebitAmountFailureAction =
  (
    err: unknown,
  ): GetVirtualCardsStatisticsClearedTransactionDebitAmountFailureAction => ({
    type: GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE,
    payload: err,
  })

export type GetVirtualCardsStatisticsClearedTransactionDebitAmountActions =
  | GetVirtualCardsStatisticsClearedTransactionDebitAmountInitAction
  | GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessAction
  | GetVirtualCardsStatisticsClearedTransactionDebitAmountFailureAction

export const getVirtualCardsStatisticsClearedTransactionDebitAmount = (
  virtualCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetVirtualCardsStatisticsClearedTransactionDebitAmountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetVirtualCardsStatisticsClearedTransactionDebitAmountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(
      createGetVirtualCardsStatisticsClearedTransactionDebitAmountInitAction(),
    )

    return axios
      .post<GetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessPayload>(
        `/statistics`,
        {
          statistics: virtualCardIds.map((vcId) => ({
            agg: StatisticAggregateType.Sum,
            type: StatisticModelType.Transaction,
            query: {
              virtualCardId: [vcId],
              type: TransactionType.Debit,
              field:
                TransactionStatisticQueryFieldType.clearingBillingAmountCents,
              status: [TransactionStatus.Cleared],
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetVirtualCardsStatisticsClearedTransactionDebitAmountSuccessAction(
              {
                ...response.data,
                virtualCardIds: virtualCardIds,
                statisticKeys: ['clearedTransactionDebitAmount'],
              },
            ),
          )
        },
        (err) => {
          dispatch(
            createGetVirtualCardsStatisticsClearedTransactionDebitAmountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT =
  'data/virtualCards/GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT'
export const GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS =
  'data/virtualCards/GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS'
export const GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE =
  'data/virtualCards/GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE'

export interface GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessPayload {
  statistics: Array<{ value: number }>
  virtualCardIds: string[]
  statisticKeys: Array<keyof VirtualCardStatistics>
}
export interface GetVirtualCardsStatisticsClearedTransactionCreditAmountInitAction {
  type: typeof GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT
}
export interface GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS
  payload: GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessPayload
}
export interface GetVirtualCardsStatisticsClearedTransactionCreditAmountFailureAction {
  type: typeof GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE
  payload: unknown
}

const createGetVirtualCardsStatisticsClearedTransactionCreditAmountInitAction =
  (): GetVirtualCardsStatisticsClearedTransactionCreditAmountInitAction => ({
    type: GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT,
  })
const createGetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessAction =
  (
    payload: GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessPayload,
  ): GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessAction => ({
    type: GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS,
    payload,
  })
const createGetVirtualCardsStatisticsClearedTransactionCreditAmountFailureAction =
  (
    err: unknown,
  ): GetVirtualCardsStatisticsClearedTransactionCreditAmountFailureAction => ({
    type: GET_VIRTUAL_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE,
    payload: err,
  })

export type GetVirtualCardsStatisticsClearedTransactionCreditAmountActions =
  | GetVirtualCardsStatisticsClearedTransactionCreditAmountInitAction
  | GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessAction
  | GetVirtualCardsStatisticsClearedTransactionCreditAmountFailureAction

export const getVirtualCardsStatisticsClearedTransactionCreditAmount = (
  virtualCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetVirtualCardsStatisticsClearedTransactionCreditAmountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetVirtualCardsStatisticsClearedTransactionCreditAmountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(
      createGetVirtualCardsStatisticsClearedTransactionCreditAmountInitAction(),
    )

    return axios
      .post<GetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessPayload>(
        `/statistics`,
        {
          statistics: virtualCardIds.map((vcId) => ({
            agg: StatisticAggregateType.Sum,
            type: StatisticModelType.Transaction,
            query: {
              virtualCardId: [vcId],
              type: TransactionType.Credit,
              field:
                TransactionStatisticQueryFieldType.clearingBillingAmountCents,
              status: [TransactionStatus.Cleared],
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetVirtualCardsStatisticsClearedTransactionCreditAmountSuccessAction(
              {
                ...response.data,
                virtualCardIds: virtualCardIds,
                statisticKeys: ['clearedTransactionCreditAmount'],
              },
            ),
          )
        },
        (err) => {
          dispatch(
            createGetVirtualCardsStatisticsClearedTransactionCreditAmountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const RESEND_VIRTUAL_CARD_EMAIL_INIT =
  'data/virtualCards/RESEND_VIRTUAL_CARD_EMAIL_INIT'
export const RESEND_VIRTUAL_CARD_EMAIL_SUCCESS =
  'data/virtualCards/RESEND_VIRTUAL_CARD_EMAIL_SUCCESS'
export const RESEND_VIRTUAL_CARD_EMAIL_FAILURE =
  'data/virtualCards/RESEND_VIRTUAL_CARD_EMAIL_FAILURE'

export type ResendVirtualCardEmailSuccessPayload = {
  emailSent: boolean
}

export type ResendVirtualCardEmailFailurePayload = BackendErrorPayload<string>

export interface ResendVirtualCardEmailInitAction {
  type: typeof RESEND_VIRTUAL_CARD_EMAIL_INIT
}
export interface ResendVirtualCardEmailSuccessAction {
  type: typeof RESEND_VIRTUAL_CARD_EMAIL_SUCCESS
  payload: ResendVirtualCardEmailSuccessPayload
}
export interface ResendVirtualCardEmailFailureAction {
  type: typeof RESEND_VIRTUAL_CARD_EMAIL_FAILURE
  payload: ResendVirtualCardEmailFailurePayload
}

export type ResendVirtualCardEmailActions =
  | ResendVirtualCardEmailInitAction
  | ResendVirtualCardEmailSuccessAction
  | ResendVirtualCardEmailFailureAction

const createResendVirtualCardEmailInitAction =
  (): ResendVirtualCardEmailInitAction => ({
    type: RESEND_VIRTUAL_CARD_EMAIL_INIT,
  })
const createResendVirtualCardEmailSuccessAction = (
  payload: ResendVirtualCardEmailSuccessPayload,
): ResendVirtualCardEmailSuccessAction => ({
  type: RESEND_VIRTUAL_CARD_EMAIL_SUCCESS,
  payload,
})
const createResendVirtualCardEmailFailureAction = (
  payload: ResendVirtualCardEmailFailurePayload,
): ResendVirtualCardEmailFailureAction => ({
  type: RESEND_VIRTUAL_CARD_EMAIL_FAILURE,
  payload,
})

export const resendVirtualCardEmail = (
  virtualCardId: string,
  name: string | undefined,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  ResendVirtualCardEmailActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, ResendVirtualCardEmailActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createResendVirtualCardEmailInitAction())
    return axios
      .post<ResendVirtualCardEmailSuccessPayload>(
        `/virtualcards/resendvirtualcardcreatedemail`,
        {
          virtualCardId: virtualCardId,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createResendVirtualCardEmailSuccessAction(response.data))
          response.data.emailSent
            ? dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <ResendVirtualCardToastSuccessMessage name={name} />
                  ),
                }),
              )
            : dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: <ResendVirtualCardToastLimitMessage />,
                  hideImage: true,
                  failureColorScheme: true,
                }),
              )
        },
        (err) => {
          dispatch(createResendVirtualCardEmailFailureAction(err.response.data))
          if (
            err.response.data.error === 'validationFailed' &&
            err.response.data.details?.length > 0 &&
            err.response.data.details[0].field ===
              'virtualCard.expenseCategories'
          ) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <ResendVirtualCardToastExpenseCategoriesRequiredButMissingMessage />
                ),
                hideImage: true,
                failureColorScheme: true,
              }),
            )
          } else {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <ResendVirtualCardToastFailureMessage />,
                hideImage: true,
                failureColorScheme: true,
              }),
            )
          }
        },
      )
  }
}

export const PUT_CLOSE_VIRTUAL_CARD_INIT =
  'data/virtualCards/PUT_CLOSE_VIRTUAL_CARD_INIT'
export const PUT_CLOSE_VIRTUAL_CARD_SUCCESS =
  'data/virtualCards/PUT_CLOSE_VIRTUAL_CARD_SUCCESS'
export const PUT_CLOSE_VIRTUAL_CARD_FAILURE =
  'data/virtualCards/PUT_CLOSE_VIRTUAL_CARD_FAILURE'

export type PutCloseVirtualCardSuccessPayload = {
  virtualCard: VirtualCardSecureResponse
}
export type PutCloseVirtualCardFailurePayload = BackendErrorPayload<string>

export interface PutCloseVirtualCardInitAction {
  type: typeof PUT_CLOSE_VIRTUAL_CARD_INIT
}
export interface PutCloseVirtualCardSuccessAction {
  type: typeof PUT_CLOSE_VIRTUAL_CARD_SUCCESS
  payload: PutCloseVirtualCardSuccessPayload
}
export interface PutCloseVirtualCardFailureAction {
  type: typeof PUT_CLOSE_VIRTUAL_CARD_FAILURE
  payload: PutCloseVirtualCardFailurePayload
}

export type PutCloseVirtualCardActions =
  | PutCloseVirtualCardInitAction
  | PutCloseVirtualCardSuccessAction
  | PutCloseVirtualCardFailureAction

const createPutCloseVirtualCardInitAction =
  (): PutCloseVirtualCardInitAction => ({
    type: PUT_CLOSE_VIRTUAL_CARD_INIT,
  })
const createPutCloseVirtualCardSuccessAction = (
  payload: PutCloseVirtualCardSuccessPayload,
): PutCloseVirtualCardSuccessAction => ({
  type: PUT_CLOSE_VIRTUAL_CARD_SUCCESS,
  payload,
})
const createPutCloseVirtualCardFailureAction = (
  response: PutCloseVirtualCardFailurePayload,
): PutCloseVirtualCardFailureAction => ({
  type: PUT_CLOSE_VIRTUAL_CARD_FAILURE,
  payload: response,
})

export const putCloseVirtualCardAndCloseModal = (
  id: string,
  displayName: string,
): ThunkAction<Promise<void>, RootState, void, PutCloseVirtualCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutCloseVirtualCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutCloseVirtualCardInitAction())
    return axios
      .put<PutCloseVirtualCardSuccessPayload>(
        `/virtualcards/${id}/close`,
        undefined,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutCloseVirtualCardSuccessAction(response.data))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CloseVirtualCardToastMessage
                  displayName={displayName}
                  id={id}
                />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createPutCloseVirtualCardFailureAction(err.response.data))
        },
      )
  }
}

export const PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT =
  'data/virtualCards/PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT'
export const PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS =
  'data/virtualCards/PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS'
export const PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE =
  'data/virtualCards/PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE'

export type PatchBulkVirtualCardUpdateSuccessPayload = {
  message: string
  bulkVirtualCardActionId: string
}

export type PatchBulkVirtualCardUpdateFailurePayload =
  BackendErrorPayload<string> & {
    status: number
    exceptionType?: SaasActionRequiredType
  }

export interface PatchBulkVirtualCardUpdateInitAction {
  type: typeof PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT
}
export interface PatchBulkVirtualCardUpdateSuccessAction {
  type: typeof PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS
  payload: PatchBulkVirtualCardUpdateSuccessPayload
}
export interface PatchBulkVirtualCardUpdateFailureAction {
  type: typeof PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE
  payload: PatchBulkVirtualCardUpdateFailurePayload
}

export type PatchBulkVirtualCardUpdateActions =
  | PatchBulkVirtualCardUpdateInitAction
  | PatchBulkVirtualCardUpdateSuccessAction
  | PatchBulkVirtualCardUpdateFailureAction

const createPatchBulkVirtualCardUpdateInitAction =
  (): PatchBulkVirtualCardUpdateInitAction => ({
    type: PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT,
  })
const createPatchBulkVirtualCardUpdateSuccessAction = (
  payload: PatchBulkVirtualCardUpdateSuccessPayload,
): PatchBulkVirtualCardUpdateSuccessAction => ({
  type: PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS,
  payload,
})
const createPatchBulkVirtualCardUpdateFailureAction = (
  body: BackendErrorPayload<string>,
  status: number,
): PatchBulkVirtualCardUpdateFailureAction => ({
  type: PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE,
  payload: { ...body, status },
})

export const patchBulkVirtualCardUpdateAndCloseModal = (
  body: PatchBulkVirtualCardUpdateRequest,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PatchBulkVirtualCardUpdateActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchBulkVirtualCardUpdateActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPatchBulkVirtualCardUpdateInitAction())
    return axios
      .patch(
        `/virtualcards/bulkupdate`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPatchBulkVirtualCardUpdateSuccessAction(response.data))
          dispatch(
            getBulkVirtualCardJobStatus(response.data.bulkVirtualCardActionId),
          )
          dispatch(createSaveSelectedVirtualCardIdsAction([]))
          dispatch(createSetIsBulkUpdateVirtualCardsFlyoutOpen(false))
        },
        (err) => {
          dispatch(
            createPatchBulkVirtualCardUpdateFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
        },
      )
  }
}

export const PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT =
  'data/virtualCards/PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT'
export const PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS =
  'data/virtualCards/PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS'
export const PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE =
  'data/virtualCards/PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE'

export type PutBulkVirtualCardDeactivateSuccessPayload = {
  bulkVirtualCardDeactivateResponse: string
  bulkVirtualCardActionId: string
}

export type PutBulkVirtualCardDeactivateFailurePayload =
  BackendErrorPayload<string>

export interface PutBulkVirtualCardDeactivateInitAction {
  type: typeof PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT
}
export interface PutBulkVirtualCardDeactivateSuccessAction {
  type: typeof PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS
  payload: PutBulkVirtualCardDeactivateSuccessPayload
}
export interface PutBulkVirtualCardDeactivateFailureAction {
  type: typeof PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE
  payload: PutBulkVirtualCardDeactivateFailurePayload
}

export type PutBulkVirtualCardDeactivateActions =
  | PutBulkVirtualCardDeactivateInitAction
  | PutBulkVirtualCardDeactivateSuccessAction
  | PutBulkVirtualCardDeactivateFailureAction

const createPutBulkVirtualCardDeactivateInitAction =
  (): PutBulkVirtualCardDeactivateInitAction => ({
    type: PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT,
  })
const createPutBulkVirtualCardDeactivateSuccessAction = (
  payload: PutBulkVirtualCardDeactivateSuccessPayload,
): PutBulkVirtualCardDeactivateSuccessAction => ({
  type: PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS,
  payload,
})
const createPutBulkVirtualCardDeactivateFailureAction = (
  response: PutBulkVirtualCardDeactivateFailurePayload,
): PutBulkVirtualCardDeactivateFailureAction => ({
  type: PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE,
  payload: response,
})

export const putBulkVirtualCardDeactivateAndCloseModal = (
  virtualCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PutBulkVirtualCardDeactivateActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PutBulkVirtualCardDeactivateActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutBulkVirtualCardDeactivateInitAction())
    return axios
      .put(
        `/virtualcards/bulkdeactivate`,
        JSON.stringify(virtualCardIds),
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createPutBulkVirtualCardDeactivateSuccessAction(response.data),
          )
          dispatch(
            getBulkVirtualCardJobStatus(response.data.bulkVirtualCardActionId),
          )
          dispatch(createSaveSelectedVirtualCardIdsAction([]))
          dispatch(createSetIsBulkDeactivateVirtualCardsPopupOpen(false))
        },
        (err) => {
          dispatch(
            createPutBulkVirtualCardDeactivateFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_BULK_VIRTUAL_CARD_JOB_STATUS_INIT =
  'data/virtualCards/GET_BULK_VIRTUAL_CARD_JOB_STATUS_INIT'
export const GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS =
  'data/virtualCards/GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS'
export const GET_BULK_VIRTUAL_CARD_JOB_STATUS_FAILURE =
  'data/virtualCards/GET_BULK_VIRTUAL_CARD_JOB_STATUS_FAILURE'

// TODO: change this
export type GetBulkVirtualCardJobStatusSuccessPayload = {
  status: BulkVirtualCardJobStatus
  bulkActionType: BulkVirtualCardJobActionType
  tasksCompleted: number
  totalTasks: number
  failedVirtualCardIds: string[]
}

export type GetBulkVirtualCardJobStatusFailurePayload =
  BackendErrorPayload<string>

export interface GetBulkVirtualCardJobStatusInitAction {
  type: typeof GET_BULK_VIRTUAL_CARD_JOB_STATUS_INIT
}
export interface GetBulkVirtualCardJobStatusSuccessAction {
  type: typeof GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS
  payload: GetBulkVirtualCardJobStatusSuccessPayload
}
export interface GetBulkVirtualCardJobStatusFailureAction {
  type: typeof GET_BULK_VIRTUAL_CARD_JOB_STATUS_FAILURE
  payload: GetBulkVirtualCardJobStatusFailurePayload
}

export type GetBulkVirtualCardJobStatusActions =
  | GetBulkVirtualCardJobStatusInitAction
  | GetBulkVirtualCardJobStatusSuccessAction
  | GetBulkVirtualCardJobStatusFailureAction

const createGetBulkVirtualCardJobStatusInitAction =
  (): GetBulkVirtualCardJobStatusInitAction => ({
    type: GET_BULK_VIRTUAL_CARD_JOB_STATUS_INIT,
  })
const createGetBulkVirtualCardJobStatusSuccessAction = (
  payload: GetBulkVirtualCardJobStatusSuccessPayload,
): GetBulkVirtualCardJobStatusSuccessAction => ({
  type: GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS,
  payload,
})
const createGetBulkVirtualCardJobStatusFailureAction = (
  response: GetBulkVirtualCardJobStatusFailurePayload,
): GetBulkVirtualCardJobStatusFailureAction => ({
  type: GET_BULK_VIRTUAL_CARD_JOB_STATUS_FAILURE,
  payload: response,
})

export const getBulkVirtualCardJobStatus = (
  id: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetBulkVirtualCardJobStatusActions | PatchBulkVirtualCardUpdateSuccessAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      | GetBulkVirtualCardJobStatusActions
      | PatchBulkVirtualCardUpdateSuccessAction
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetBulkVirtualCardJobStatusInitAction())
    return axios
      .get<GetBulkVirtualCardJobStatusSuccessPayload>(
        `/bulkvirtualcardactions/status?virtualCardActionId=${id}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const {
            status,
            bulkActionType,
            tasksCompleted,
            totalTasks,
            failedVirtualCardIds,
          } = response.data

          dispatch(
            createGetBulkVirtualCardJobStatusSuccessAction(response.data),
          )

          if (bulkActionType === BulkVirtualCardJobActionType.Update) {
            if (status === BulkVirtualCardJobStatus.InProgress) {
              // in progress
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <UpdateBulkVirtualCardInProgressToastMessage
                      updating={tasksCompleted}
                      total={totalTasks}
                    />
                  ),
                  inProgressColorScheme: true,
                  image: (
                    <Processing
                      css={css`
                        transform: scale(1.2);
                      `}
                    />
                  ),
                  bulkToast: true,
                  isInfinite: true,
                }),
              )
            } else {
              // success or failure
              if (failedVirtualCardIds.length > 0) {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: (
                      <UpdateBulkVirtualCardPartialCompleteToastMessage
                        updated={tasksCompleted}
                        total={totalTasks}
                      />
                    ),
                    partialSuccessColorScheme: true,
                    image: (
                      <SvgAlertCircledFilled2
                        css={css`
                          transform: scale(0.8);
                          margin-left: -10px;
                        `}
                      />
                    ),
                  }),
                )
              } else {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: <UpdateBulkVirtualCardCompleteToastMessage />,
                  }),
                )
              }
            }
          } else if (
            bulkActionType === BulkVirtualCardJobActionType.Deactivate
          ) {
            if (status === BulkVirtualCardJobStatus.InProgress) {
              // in progress
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <DeactivateBulkVirtualCardInProgressToastMessage
                      deactivating={tasksCompleted}
                      total={totalTasks}
                    />
                  ),
                  inProgressColorScheme: true,
                  image: (
                    <Processing
                      css={css`
                        transform: scale(1.2);
                      `}
                    />
                  ),
                  bulkToast: true,
                  isInfinite: true,
                }),
              )
            } else {
              if (failedVirtualCardIds.length > 0) {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: (
                      <DeactivateBulkVirtualCardPartialCompleteToastMessage
                        deactivated={tasksCompleted}
                        total={totalTasks}
                      />
                    ),
                    partialSuccessColorScheme: true,
                    image: (
                      <SvgAlertCircledFilled2
                        css={css`
                          transform: scale(0.8);
                          margin-left: -10px;
                        `}
                      />
                    ),
                  }),
                )
              } else {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: (
                      <DeactivateBulkVirtualCardCompleteToastMessage />
                    ),
                  }),
                )
              }
            }
          } else if (bulkActionType === BulkVirtualCardJobActionType.Close) {
            if (status === BulkVirtualCardJobStatus.InProgress) {
              // in progress
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <CloseBulkVirtualCardInProgressToastMessage
                      closing={tasksCompleted}
                      total={totalTasks}
                    />
                  ),
                  inProgressColorScheme: true,
                  image: (
                    <Processing
                      css={css`
                        transform: scale(1.2);
                      `}
                    />
                  ),
                  bulkToast: true,
                  isInfinite: true,
                }),
              )
            } else {
              if (failedVirtualCardIds.length > 0) {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: (
                      <CloseBulkVirtualCardPartialCompleteToastMessage
                        closed={tasksCompleted}
                        total={totalTasks}
                      />
                    ),
                    partialSuccessColorScheme: true,
                    image: (
                      <SvgAlertCircledFilled2
                        css={css`
                          transform: scale(0.8);
                          margin-left: -10px;
                        `}
                      />
                    ),
                  }),
                )
              } else {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: <CloseBulkVirtualCardCompleteToastMessage />,
                  }),
                )
              }
            }
          }
        },
        (err) => {
          dispatch(
            createGetBulkVirtualCardJobStatusFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_INIT =
  'data/virtualCards/GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_INIT'
export const GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_FAILURE =
  'data/virtualCards/GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_FAILURE'

export type GetBulkVirtualCardJobStatusByTaskIdSuccessPayload = {
  bulkVirtualCardActionId: string
}

export type GetBulkVirtualCardJobStatusByTaskIdFailurePayload =
  BackendErrorPayload<string>

export interface GetBulkVirtualCardJobStatusByTaskIdInitAction {
  type: typeof GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_INIT
}
export interface GetBulkVirtualCardJobStatusByTaskIdFailureAction {
  type: typeof GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_FAILURE
  payload: GetBulkVirtualCardJobStatusByTaskIdFailurePayload
}

export type GetBulkVirtualCardJobStatusByTaskIdActions =
  | GetBulkVirtualCardJobStatusByTaskIdInitAction
  | GetBulkVirtualCardJobStatusByTaskIdFailureAction

const createGetBulkVirtualCardJobStatusByTaskIdInitAction =
  (): GetBulkVirtualCardJobStatusByTaskIdInitAction => ({
    type: GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_INIT,
  })
const createGetBulkVirtualCardJobStatusByTaskIdFailureAction = (
  response: GetBulkVirtualCardJobStatusByTaskIdFailurePayload,
): GetBulkVirtualCardJobStatusByTaskIdFailureAction => ({
  type: GET_BULK_VIRTUAL_CARD_JOB_STATUS_BY_TASK_ID_FAILURE,
  payload: response,
})

export const getBulkVirtualCardJobStatusByTaskId = (
  id: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetBulkVirtualCardJobStatusByTaskIdActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetBulkVirtualCardJobStatusByTaskIdActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetBulkVirtualCardJobStatusByTaskIdInitAction())
    return axios
      .get<GetBulkVirtualCardJobStatusByTaskIdSuccessPayload>(
        `/bulkvirtualcardactions/${id}/action`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            getBulkVirtualCardJobStatus(response.data.bulkVirtualCardActionId),
          )
        },
        (err) => {
          dispatch(
            createGetBulkVirtualCardJobStatusByTaskIdFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const PUT_BULK_VIRTUAL_CARD_CLOSE_INIT =
  'data/virtualCards/PUT_BULK_VIRTUAL_CARD_CLOSE_INIT'
export const PUT_BULK_VIRTUAL_CARD_CLOSE_SUCCESS =
  'data/virtualCards/PUT_BULK_VIRTUAL_CARD_CLOSE_SUCCESS'
export const PUT_BULK_VIRTUAL_CARD_CLOSE_FAILURE =
  'data/virtualCards/PUT_BULK_VIRTUAL_CARD_CLOSE_FAILURE'

export type PutBulkVirtualCardCloseSuccessPayload = {
  bulkVirtualCardCloseResponse: string
  bulkVirtualCardActionId: string
}

export type PutBulkVirtualCardCloseFailurePayload = BackendErrorPayload<string>

export interface PutBulkVirtualCardCloseInitAction {
  type: typeof PUT_BULK_VIRTUAL_CARD_CLOSE_INIT
}

export interface PutBulkVirtualCardCloseSuccessAction {
  type: typeof PUT_BULK_VIRTUAL_CARD_CLOSE_SUCCESS
  payload: PutBulkVirtualCardCloseSuccessPayload
}

export interface PutBulkVirtualCardCloseFailureAction {
  type: typeof PUT_BULK_VIRTUAL_CARD_CLOSE_FAILURE
  payload: PutBulkVirtualCardCloseFailurePayload
}

export type PutBulkVirtualCardCloseActions =
  | PutBulkVirtualCardCloseInitAction
  | PutBulkVirtualCardCloseSuccessAction
  | PutBulkVirtualCardCloseFailureAction

const createPutBulkVirtualCardCloseInitAction =
  (): PutBulkVirtualCardCloseInitAction => ({
    type: PUT_BULK_VIRTUAL_CARD_CLOSE_INIT,
  })

const createPutBulkVirtualCardCloseSuccessAction = (
  payload: PutBulkVirtualCardCloseSuccessPayload,
): PutBulkVirtualCardCloseSuccessAction => ({
  type: PUT_BULK_VIRTUAL_CARD_CLOSE_SUCCESS,
  payload,
})

const createPutBulkVirtualCardCloseFailureAction = (
  response: PutBulkVirtualCardCloseFailurePayload,
): PutBulkVirtualCardCloseFailureAction => ({
  type: PUT_BULK_VIRTUAL_CARD_CLOSE_FAILURE,
  payload: response,
})

export const putBulkVirtualCardCloseAndCloseModal = (
  virtualCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PutBulkVirtualCardCloseActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutBulkVirtualCardCloseActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutBulkVirtualCardCloseInitAction())
    return axios
      .put(
        `/virtualcards/bulkclose`,
        JSON.stringify(virtualCardIds),
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutBulkVirtualCardCloseSuccessAction(response.data))
          dispatch(
            getBulkVirtualCardJobStatus(response.data.bulkVirtualCardActionId),
          )
          dispatch(createSaveSelectedVirtualCardIdsAction([]))
          dispatch(createSetIsBulkCloseVirtualCardsPopupOpen(false))
        },
        (err) => {
          dispatch(
            createPutBulkVirtualCardCloseFailureAction(err.response.data),
          )
        },
      )
  }
}
